import React, { useState } from 'react';
import SousNavPV from './SousNavPV';
import { Link } from 'react-router-dom';
import { SiteLayout } from './SiteLayout';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL as API } from '../../config';

const TYPES = ['Ombrière', 'Hangar agricole / bac acier', 'Toiture plate', 'Centrale au sol', 'Électricité / réseaux / éclairage', 'Pompe à chaleur / chauffe-eau / chaudière', 'Bornes de recharge (IRVE)', 'FTTH / fibre optique', 'Autre'];
const MAX_TOTAL = 3 * 1024 * 1024; // 3 Mo au total (limite serveur)

// Réduit les photos avant envoi (1600 px max, JPEG) ; les PDF passent tels quels
export const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onerror = reject;
  if (!file.type.startsWith('image/')) { r.onload = () => resolve({ name: file.name, data: r.result, size: file.size }); r.readAsDataURL(file); return; }
  r.onload = () => {
    const img = new Image();
    img.onerror = () => resolve({ name: file.name, data: r.result, size: file.size });
    img.onload = () => {
      const k = Math.min(1, 1600 / Math.max(img.width, img.height));
      const c = document.createElement('canvas'); c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      const data = c.toDataURL('image/jpeg', 0.75);
      resolve({ name: file.name.replace(/\.[^.]+$/, '') + '.jpg', data, size: Math.round(data.length * 0.75) });
    };
    img.src = r.result;
  };
  r.readAsDataURL(file);
});

const PRESTA = ['Pose structures', 'Pose modules', 'Chemins de câbles', 'Câblage DC/AC', 'Mise en service', 'Chantier complet'];
const REGIONS = ['Nouvelle-Aquitaine', 'Occitanie', 'Île-de-France', 'Auvergne-Rhône-Alpes', "Provence-Alpes-Côte d'Azur", 'Pays de la Loire', 'Bretagne', 'Normandie', 'Hauts-de-France', 'Grand Est', 'Bourgogne-Franche-Comté', 'Centre-Val de Loire', 'Corse'];

export const SiteDevis = () => {
  // Pré-remplissage depuis le simulateur (/devis?type=…&kwc=…&presta=…)
  const qs = (() => { try { return new URLSearchParams(window.location.search); } catch (e) { return new URLSearchParams(); } })();
  const qType = qs.get('type') || '';
  const [f, setF] = useState({ societe:'', contact:'', email:'', phone:'', type: TYPES.includes(qType) ? qType : '', puissance: qs.get('kwc') || '', region:'', date:'', presta: (qs.get('presta') || '').split(',').filter(p => PRESTA.includes(p)), message: qs.get('kwc') ? 'Estimation faite avec le simulateur.' : '', modules:'', fixation:'', limite:'', lien:'' });
  const [files, setFiles] = useState([]);
  const totalSize = files.reduce((a, x) => a + x.size, 0);
  const addFiles = async (list) => {
    const arr = Array.from(list || []).slice(0, 5 - files.length);
    const out = [];
    for (const fl of arr) {
      if (!/^image\//.test(fl.type) && fl.type !== 'application/pdf') { toast.error(`${fl.name} : format non accepté (PDF ou photo)`); continue; }
      try { out.push(await readFile(fl)); } catch { toast.error(`${fl.name} : lecture impossible`); }
    }
    const next = [...files, ...out];
    if (next.reduce((a, x) => a + x.size, 0) > MAX_TOTAL) { toast.error('Fichiers trop lourds (3 Mo max au total). Envoyez-les plutôt par email.'); return; }
    setFiles(next);
  };
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));
  const toggle = (p) => setF(x => ({ ...x, presta: x.presta.includes(p) ? x.presta.filter(y => y !== p) : [...x.presta, p] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.societe || !f.email || !f.phone || !f.type) { toast.error('Remplissez les champs obligatoires'); return; }
    setLoading(true);
    try {
      await axios.post(API + '/config/candidates/apply', {
        name: f.contact ? `${f.contact} (${f.societe})` : f.societe,
        email: f.email, phone: f.phone, zone: f.region,
        experience: `SOUS-TRAITANCE | ${f.type} | ${f.puissance || '?'} kWc | ${f.modules ? f.modules + ' modules | ' : ''}${f.fixation ? 'Fixation: ' + f.fixation + ' | ' : ''}${f.presta.join(', ')}`,
        message: `Démarrage: ${f.date || 'n.c.'}${f.limite ? ` | Remise de l'offre avant le ${f.limite}` : ''}${f.lien ? ` | Dossier/DCE : ${f.lien}` : ''}${files.length ? ` | ${files.length} pièce(s) jointe(s)` : ''} | ${f.message}`,
        attachments: files.map(x => ({ name: x.name, data: x.data })),
      });
      setSent(true);
    } catch {
      toast.error('Erreur envoi. Appelez le 06 66 88 77 66');
    }
    setLoading(false);
  };

  const inp = { width:'100%', padding:'14px 16px', border:'1px solid var(--ev-line)', borderRadius:4, fontSize:15, outline:'none', fontFamily:'inherit', background:'#fff' };
  const lab = { display:'block', fontFamily:'var(--ev-h)', fontWeight:700, fontSize:'.72rem', letterSpacing:'.1em', textTransform:'uppercase', color:'var(--ev-navy)', marginBottom:8 };

  return (
    <SiteLayout>
      <section style={{ background:'var(--ev-navy2)', padding:'100px 0 80px' }}>
        <div className="ev-wrap">
          <span className="ev-kicker" style={{ color:'var(--ev-sun)' }}>EPC · Installateurs · Développeurs</span>
          <h1 className="ev-h1" style={{ fontWeight:900, fontSize:'clamp(2rem,5vw,3.4rem)', color:'#fff', textTransform:'uppercase', lineHeight:1.05 }}>
            Consultez-nous pour <span style={{ color:'var(--ev-sun)' }}>votre chantier</span>
          </h1>
          <p style={{ color:'rgba(255,255,255,.7)', marginTop:16, maxWidth:620, lineHeight:1.8 }}>
            Donneurs d'ordre : décrivez votre chantier de sous-traitance, nous revenons vers vous avec un chiffrage sous 48 h ouvrées. <a href={`${process.env.PUBLIC_URL}/docs/EVOLINK-plaquette-sous-traitance-PV.pdf`} target="_blank" rel="noopener noreferrer" style={{ color:'var(--ev-sun)', fontWeight:800 }}>📄 Notre plaquette (PDF)</a>. Vous êtes un particulier ? <Link to="/particuliers" style={{ color:'var(--ev-sun)', fontWeight:800 }}>C'est par ici →</Link>
          </p>
        </div>
      </section>
      <SousNavPV />

      <section style={{ background:'#FFFBEB', borderBottom:'1px solid #FDE68A', padding:'16px 0' }}>
        <div className="ev-wrap" style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center' }}>
          <b style={{ color:'var(--ev-navy)' }}>Vous n'êtes pas donneur d'ordre ?</b>
          <Link to="/particuliers#demande" className="ev-btn" style={{ border:'2px solid var(--ev-navy)', color:'var(--ev-navy)', padding:'8px 14px' }}>🏠 Particulier : devis maison</Link>
          <Link to="/professionnels#demande" className="ev-btn" style={{ border:'2px solid var(--ev-navy)', color:'var(--ev-navy)', padding:'8px 14px' }}>🏢 Entreprise : devis pro</Link>
        </div>
      </section>
      <section className="ev-sec alt">
        <div className="ev-wrap" style={{ maxWidth:860 }}>
          {sent ? (
            <div style={{ background:'#fff', padding:'60px 30px', textAlign:'center', boxShadow:'0 10px 40px rgba(11,27,77,.08)' }}>
              <div style={{ fontSize:56, marginBottom:14 }}>✅</div>
              <h2 className="ev-title">Demande <span>envoyée</span></h2>
              <p className="ev-lead" style={{ margin:'16px auto 28px' }}>Merci ! Nous étudions votre projet et revenons vers vous sous 48 h ouvrées.</p>
              <Link to="/" className="ev-btn ev-btn-blue">Retour à l'accueil</Link>
            </div>
          ) : (
            <form onSubmit={submit} style={{ background:'#fff', padding:'44px 40px', boxShadow:'0 10px 40px rgba(11,27,77,.08)', borderTop:'4px solid var(--ev-sun)' }}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:20 }}>
                <div><label style={lab}>Société *</label><input aria-label="Société" style={inp} value={f.societe} onChange={e => upd('societe', e.target.value)} required /></div>
                <div><label style={lab}>Contact</label><input aria-label="Contact" style={inp} value={f.contact} onChange={e => upd('contact', e.target.value)} placeholder="Nom / fonction" /></div>
                <div><label style={lab}>Email *</label><input aria-label="Email" style={inp} type="email" value={f.email} onChange={e => upd('email', e.target.value)} required /></div>
                <div><label style={lab}>Téléphone *</label><input aria-label="Téléphone" style={inp} type="tel" value={f.phone} onChange={e => upd('phone', e.target.value)} required /></div>
                <div>
                  <label style={lab}>Type de chantier *</label>
                  <select aria-label="Type de chantier" style={inp} value={f.type} onChange={e => upd('type', e.target.value)} required>
                    <option value="">— Sélectionner —</option>
                    {TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div><label style={lab}>Puissance (kWc)</label><input aria-label="Puissance (kWc)" style={inp} value={f.puissance} onChange={e => upd('puissance', e.target.value)} placeholder="ex : 500" /></div>
                <div>
                  <label style={lab}>Région</label>
                  <select aria-label="Région" style={inp} value={f.region} onChange={e => upd('region', e.target.value)}>
                    <option value="">— Région —</option>
                    {REGIONS.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div><label style={lab}>Nombre de modules</label><input aria-label="Nombre de modules" style={inp} type="number" min="0" value={f.modules} onChange={e => upd('modules', e.target.value)} placeholder="ex : 1200" /></div>
                <div><label style={lab}>Système de fixation</label><input aria-label="Système de fixation" style={inp} value={f.fixation} onChange={e => upd('fixation', e.target.value)} placeholder="ex : K2, Esdec, JA Solar…" /></div>
                <div><label style={lab}>Démarrage souhaité</label><input aria-label="Démarrage souhaité" style={inp} type="month" value={f.date} onChange={e => upd('date', e.target.value)} /></div>
                <div><label style={lab}>Date limite de remise de l'offre</label><input aria-label="Date limite de remise de l'offre" style={inp} type="date" value={f.limite} onChange={e => upd('limite', e.target.value)} /></div>
              </div>

              <div style={{ marginTop:24 }}>
                <label style={lab}>Prestations attendues</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
                  {PRESTA.map(p => {
                    const on = f.presta.includes(p);
                    return (
                      <button type="button" key={p} onClick={() => toggle(p)}
                        style={{ padding:'10px 16px', border:`2px solid ${on ? 'var(--ev-blue)' : 'var(--ev-line)'}`, background:on ? 'var(--ev-blue)' : '#fff', color:on ? '#fff' : 'var(--ev-navy)', fontWeight:600, fontSize:14, cursor:'pointer', borderRadius:4 }}>
                        {on ? '✓ ' : ''}{p}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginTop:24 }}>
                <label style={lab}>Description du projet</label>
                <textarea aria-label="Description du projet" style={{ ...inp, resize:'vertical' }} rows={4} value={f.message} onChange={e => upd('message', e.target.value)} placeholder="Délais, contraintes d'accès, matériel fourni, levage…" />
              </div>

              <div style={{ marginTop:24 }}>
                <label style={lab}>Plans, photos, cahier des charges <span style={{ textTransform:'none', letterSpacing:0, fontWeight:500, color:'var(--ev-muted)' }}>(facultatif · PDF ou photos · 5 fichiers / 3 Mo max)</span></label>
                <label style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:10, padding:'22px 16px', border:'2px dashed var(--ev-line)', borderRadius:6, cursor:'pointer', color:'var(--ev-navy)', fontWeight:600, background:'var(--ev-bg2)' }}>
                  📎 Ajouter des fichiers
                  <input type="file" multiple accept="application/pdf,image/*" style={{ display:'none' }} onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
                </label>
                <input style={{ ...inp, marginTop:10 }} type="url" value={f.lien} onChange={e => upd('lien', e.target.value)} placeholder="Dossier lourd (DCE, plans) : collez un lien WeTransfer, Google Drive, Dropbox…" />
                {files.length > 0 && (
                  <div style={{ marginTop:10, display:'grid', gap:6 }}>
                    {files.map((x, i) => (
                      <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 12px', background:'#fff', border:'1px solid var(--ev-line)', fontSize:14 }}>
                        <span>{/pdf$/i.test(x.name) ? '📄' : '🖼'} {x.name} <span style={{ color:'var(--ev-muted)' }}>({Math.round(x.size / 1024)} Ko)</span></span>
                        <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} style={{ border:'none', background:'none', color:'#DC2626', fontWeight:700, cursor:'pointer' }}>✕</button>
                      </div>
                    ))}
                    <span style={{ fontSize:12, color:'var(--ev-muted)' }}>Total : {(totalSize / 1024 / 1024).toFixed(1)} Mo / 3 Mo</span>
                  </div>
                )}
              </div>

              <button type="submit" disabled={loading} className="ev-btn ev-btn-sun" style={{ marginTop:28, width:'100%', justifyContent:'center', opacity:loading ? .6 : 1 }}>
                {loading ? 'Envoi…' : 'Envoyer ma demande →'}
              </button>
              <p style={{ fontSize:12, color:'var(--ev-muted)', marginTop:14, textAlign:'center' }}>
                Réservé aux professionnels · Ou appelez-nous : <a href="tel:+33666887766" style={{ color:'var(--ev-blue)', fontWeight:700 }}>06 66 88 77 66</a>
              </p>
            </form>
          )}
        </div>
      </section>
    </SiteLayout>
  );
};

export default SiteDevis;
